/* eslint-disable no-shadow */
import { yupResolver } from '@hookform/resolvers/yup'
import React, { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { Field } from '../../../../../components/FormComponents/Field'
import { actions as receivementActions } from '../../../ReceivementState'
import { useDispatch } from 'react-redux'
import { FieldDropdown } from '../../../../../components/FormComponents/FieldDropdown'
import { updateBatchCount } from '../../../../../services/receivement/divergenceService'
import Submit from '../Components/Submit'
import { Fieldset } from './Fieldset'
import { Form } from './Form'
import InformationField from '../../../components/InformationField'

const defaultCountSchema = yup
  .number()
  .required('Campo obrigatório!')
  .integer('Quantidade deve ser um número inteiro!')
  .min(0, 'Valor deve ser no mínimo 1!')

const schema = yup.object().shape({
  qtdCaixas: defaultCountSchema,
  caixaria: defaultCountSchema,
  avulso: defaultCountSchema,
  tipoAvaria: yup.string().required('Campo obrigatório!'),
  qtdAvaria: yup
    .number()
    .required('Campo obrigatório!')
    .when('tipoAvaria', {
      is: 'NENHUM',
      then: yup.number().equals([0], 'Valor deve ser 0!'),
      otherwise: yup.number().min(1, 'Valor deve ser no mínimo 1!')
    })
    .when(
      ['qtdCaixas', 'caixaria', 'avulso'],
      (qtdCaixas, caixaria, avulso, schema) => {
        const total = qtdCaixas * caixaria + avulso
        return schema.max(
          total,
          `Quantidade de avaria deve ser menor ou igual a total`
        )
      }
    )
})

const FormRecount = ({ batchId, batchDueDate, badge, product, onSuccess, onFailure }) => {
  const { control, handleSubmit, setFocus, watch, setValue, formState, reset } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      tipoAvaria: 'NENHUM',
      qtdAvaria: '0',
    },
  })

  const [isDueDateExpired, setDueDateExpired] = useState(false)
  const [disabledByProduct, setDisabledByProduct] = useState(true)

  const dispatch = useDispatch()

  useEffect(() => {
    const currentDate = new Date()
    currentDate.setHours(0, 0, 0, 0)
    if (batchDueDate) {
      const expired = batchDueDate < currentDate
      if (expired) {
        setValue('tipoAvaria', 'VENCIDO')
      }
      setDueDateExpired(expired)
    } else {
      setDueDateExpired(false)
    }
  }, [batchDueDate])

  const qtdCaixas = watch('qtdCaixas', 0)
  const caixaria = watch('caixaria', 0)
  const avulso = watch('avulso', 0)

  useEffect(() => {
    if (isDueDateExpired) {
      setValue('qtdAvaria', formatterBR(qtdCaixas, caixaria, avulso))
    }
  }, [qtdCaixas, caixaria, avulso])

  const onSubmit = async (data) => {
    if (formState.isSubmitting) {
      return
    }

    const total = data.qtdCaixas * data.caixaria + data.avulso

    const result = await updateBatchCount(
      badge,
      batchId,
      total,
      data.tipoAvaria,
      parseInt(data.qtdAvaria)
    )
    reset()

    if (result.isOk) {
      await onSuccess()
    } else {
      onFailure(result?.data?.message || 'Falha ao atualizar a contagem do lote!')
    }
  }

  const enterKeyCode = 13
  const tabKeyCode = 9

  const handleOnKeyDown = (event, nextField) => {
    if ([enterKeyCode, tabKeyCode].includes(event.keyCode) && !event.shiftKey) {
      event.preventDefault()
      setFocus(nextField)
    }
  }

  const onSuccessValidateBarcode = () => {
    setDisabledByProduct(false)
    setTimeout(() => setFocus('qtdCaixas'), 100)
  }

  const handleOnKeyDownBarcode = (event) => {
    if ([enterKeyCode, tabKeyCode].includes(event.keyCode) && !event.shiftKey) {
      event.preventDefault()

      const idModel = product?.id_modelo
      const value = event.target.value
      const payload = { barcode: value, idModel , onSuccess: onSuccessValidateBarcode }

      if (value && idModel) {
        dispatch(receivementActions.validateProductBarcode(payload))
      }
    }
  }

  const formatterBR = useCallback((qtdCaixas, caixaria, avulso) => {
    return (
      ((qtdCaixas && caixaria) || avulso) &&
      new Intl.NumberFormat('pt-BR').format(
        parseInt(qtdCaixas || 0) * parseInt(caixaria || 0) +
          parseInt(avulso || 0)
      )
    )
  }, [])

  return (
    <>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Fieldset>
          <Field
            labelText={`Bipe produto: ${product?.descricao_produto}`}
            name="barcode"
            control={control}
            type="text"
            onKeyDown={(e) => handleOnKeyDownBarcode(e)}
          />
        </Fieldset>
        <Fieldset>
          <Field
            labelText="Qtd. caixas"
            name="qtdCaixas"
            control={control}
            type="number"
            onKeyDown={(e) => handleOnKeyDown(e, 'caixaria')}
            disabled={disabledByProduct}
          />
          <Field
            labelText="Caixaria"
            name="caixaria"
            control={control}
            type="number"
            onKeyDown={(e) => handleOnKeyDown(e, 'avulso')}
            disabled={disabledByProduct}
          />
        </Fieldset>
        <Fieldset>
          <Field
            labelText="Avulso"
            name="avulso"
            control={control}
            type="number"
            onKeyDown={(e) => handleOnKeyDown(e, isDueDateExpired ? 'qtdAvaria' : 'tipoAvaria')}
            disabled={disabledByProduct}
          />
          <Field
            labelText="Total"
            name="total"
            control={control}
            readOnly
            value={formatterBR(qtdCaixas, caixaria, avulso) || ''}
            disabled={disabledByProduct}
          />
        </Fieldset>
        <Fieldset>
          {isDueDateExpired ? (
            <InformationField
              title='Tipo avaria'
              tooltipTitleMessage='Atenção: Lote vencido!'
              tooltipTitleAlertColor='#FF0000'
              info='VENCIDO'
              tooltipInfoMessage='Atenção: Lote vencido!'
              sxProps={{
                container: {
                  gap: '.1rem',
                  marginTop: '.15rem',
                },
                title: {
                  fontSize: '16px',
                  paddingLeft: '.2rem',
                },
                containerInfo: {
                  backgroundColor: '#F0F0F0',
                },
              }}
              testId='automatic-due-date-damage-test-id'
            />
          ) : (
            <FieldDropdown
              labelText="Tipo avaria"
              name="tipoAvaria"
              options={['NENHUM', 'DANO']}
              control={control}
              onKeyDown={(e) => handleOnKeyDown(e, 'qtdAvaria')}
              disabled={disabledByProduct}
            />
          )}
          <Field
            labelText="Qtd. avaria"
            name="qtdAvaria"
            control={control}
            type="number"
            readOnly={isDueDateExpired}
            disabled={disabledByProduct}
          />
        </Fieldset>

        <Submit
          textWhenEnabled="Incluir"
          textWhenDisabled="Salvando..."
          disabled={formState.isSubmitting}
        />
      </Form>
    </>
  )
}

export { FormRecount }
